"use client";
import { useCallback, useEffect, useRef, useState } from "react";
import { requestJson } from "@/lib/clientRequest";

type Result<T> = { key: string; data?: T; error?: string };
type Options<T> = {
  interval?: number;
  debounce?: number;
  /** Runs with every successful load, batched with the data update. */
  onData?: (data: T) => void;
};
/** Loads only mounted views, cancels old scopes, and never overlaps background polls. */
export function useJsonResource<T>(
  url: string | null,
  { interval = 0, debounce = 0, onData }: Options<T> = {},
) {
  const [result, setResult] = useState<Result<T> | null>(null);
  const [pending, setPending] = useState(false);
  const request = useRef<AbortController | null>(null);
  // The listener sees the latest render's closure without resubscribing the loader.
  const dataListener = useRef(onData);
  useEffect(() => {
    dataListener.current = onData;
  });
  const load = useCallback(
    async (replacePending: boolean) => {
      if (!url || (request.current && !replacePending)) return;
      // Explicit refreshes follow mutations and retries: an older GET must not win.
      request.current?.abort();
      const controller = new AbortController();
      request.current = controller;
      setPending(true);
      try {
        const data = await requestJson<T>(url, {
          signal: controller.signal,
          fallback: (response) => `Could not load data (${response.status}).`,
        });
        setResult({ key: url, data });
        dataListener.current?.(data);
      } catch (cause) {
        if (!controller.signal.aborted)
          setResult((previous) => ({
            key: url,
            data: previous?.key === url ? previous.data : undefined,
            error:
              cause instanceof Error ? cause.message : "Could not load data.",
          }));
      } finally {
        if (request.current === controller) {
          request.current = null;
          setPending(false);
        }
      }
    },
    [url],
  );
  const refresh = useCallback(() => load(true), [load]);
  useEffect(() => {
    const timer = setTimeout(() => void load(false), debounce);
    const poll = () => {
      if (!document.hidden) void load(false);
    };
    const ticker = interval ? setInterval(poll, interval) : undefined;
    if (interval) document.addEventListener("visibilitychange", poll);
    return () => {
      clearTimeout(timer);
      clearInterval(ticker);
      document.removeEventListener("visibilitychange", poll);
      request.current?.abort();
      request.current = null;
    };
  }, [load, interval, debounce]);
  const current = result?.key === url ? result : null;
  return {
    data: current?.data,
    error: current?.error,
    loading: Boolean(url) && (!current || pending),
    refresh,
  };
}
